import { RefObject, useCallback, useEffect } from 'react'

type RefType = RefObject<HTMLElement | null>

function useClickOutside($ref: RefType | RefType[], cb: (e?: Event) => void, enabled = true) {
  const onClick = useCallback(
    (e: Event) => {
      if (!enabled) return

      const target = e.target as HTMLElement

      // target이 html, body 등 최상위 요소인 경우 무시 (Radix UI 내부 이벤트 처리로 인해 발생할 수 있음)
      if (target === document.documentElement || target === document.body) return

      // Radix UI Portal 내부 클릭은 outside로 처리하지 않음
      const radixPortal = target.closest('[data-radix-popper-content-wrapper]')
      if (radixPortal) return

      // react-calendar 내부 클릭은 outside로 처리하지 않음
      const reactCalendar = target.closest('.react-calendar')
      if (reactCalendar) return

      // 포털로 렌더되는 멀티셀렉트 드롭다운 내부 클릭은 outside로 처리하지 않음
      // (패널 등 click-outside 컨테이너 안에서 포털 드롭다운을 클릭하면 컨테이너가 닫히는 문제 방지)
      const portalDropdown = target.closest('[data-dropdown-container]')
      if (portalDropdown) return

      const refs = Array.isArray($ref) ? $ref : [$ref]
      const isOutside = refs.every((ref) => {
        const $el = ref?.current
        if (!$el) {
          return true
        }

        if ($el instanceof HTMLElement) {
          return !$el.contains(target)
        }

        return true
      })

      if (isOutside) {
        cb(e)
      }
    },
    [enabled, $ref, cb],
  )

  useEffect(() => {
    if (!enabled) return

    document.addEventListener('click', onClick)
    return () => {
      document.removeEventListener('click', onClick)
    }
  }, [onClick, enabled])
}

export default useClickOutside
